import { caseInsensitiveEquals, caseInsensitiveIncludes } from 'features/common/utils/strings'
import { SpatialDataType, SpatialIndexColumn, TilingScheme } from '../types'
import { SchemaField, SchemaFieldType } from 'features/data-explorer/types'
import { ProviderType } from 'features/connections'
import { MapType } from 'features/maps/types'

const spatialIndexSupportedTypes: string[] = [SchemaFieldType.Number, SchemaFieldType.String, SchemaFieldType.Bigint]

const defaultSpatialSettingsRules: Array<
  (field: SchemaField) =>
    | {
        hasSpatialIndex: boolean
        spatialDataColumn: string
        spatialDataType: SpatialDataType
      }
    | false
> = [
  // so the priority is
  //
  // anything matching h3 and similar names
  // anything matching quadbin
  // geom field of type geometry
  //   .. every other field of type geometry
  //
  (field) =>
    spatialIndexSupportedTypes.includes(field.type) &&
    caseInsensitiveIncludes(SpatialIndexColumn.h3, field.name) && {
      spatialDataType: SpatialDataType.H3,
      spatialDataColumn: field.name,
      hasSpatialIndex: true
    },
  (field) =>
    spatialIndexSupportedTypes.includes(field.type) &&
    caseInsensitiveIncludes(SpatialIndexColumn.quadbin, field.name) && {
      spatialDataType: SpatialDataType.Quadbin,
      spatialDataColumn: field.name,
      hasSpatialIndex: true
    },
  (field) =>
    caseInsensitiveEquals(field.name, 'geom') &&
    field.type === SchemaFieldType.Geometry && {
      spatialDataType: SpatialDataType.Geo,
      spatialDataColumn: field.name,
      hasSpatialIndex: false
    },
  (field) =>
    field.type === SchemaFieldType.Geometry && {
      spatialDataType: SpatialDataType.Geo,
      spatialDataColumn: field.name,
      hasSpatialIndex: false
    }
]

export const getDefaultSpatialTypeForColumn = (field: SchemaField): SpatialDataType | undefined => {
  for (const rule of defaultSpatialSettingsRules) {
    const result = rule(field)
    if (result) {
      return result.spatialDataType
    }
  }
}

export function spatialDataTypeCompatible(fieldType: SchemaFieldType, spatialDataType: SpatialDataType) {
  if (spatialDataType === SpatialDataType.Geo) {
    return fieldType === SchemaFieldType.Geometry
  } else {
    return spatialIndexSupportedTypes.includes(fieldType)
  }
}

/**
 * Determine spatialDataColumn,spatialDataType for data schema.
 */
export function getSpatialDataSettingsFromSchema(schema: SchemaField[]): {
  hasSpatialIndex: boolean
  spatialDataColumn: string
  spatialDataType: SpatialDataType
} | null {
  for (const rule of defaultSpatialSettingsRules) {
    for (const field of schema) {
      const result = rule(field)
      if (result) {
        return result
      }
    }
  }

  return null
}

export function getAvailableSpatialDataTypes({
  schema,
  provider,
  mapType,
  selectedColumn
}: {
  schema: SchemaField[]
  provider: ProviderType
  mapType: MapType
  selectedColumn?: string
}): SpatialDataType[] {
  if (selectedColumn) {
    const selectedFieldType = schema.find((field) => caseInsensitiveEquals(field.name, selectedColumn))?.type

    if (
      selectedFieldType === SchemaFieldType.Geometry &&
      !(provider === ProviderType.DATABRICKS_REST && mapType === MapType.query)
    ) {
      return [SpatialDataType.Geo]
    } else if (selectedFieldType && spatialIndexSupportedTypes.includes(selectedFieldType)) {
      return [SpatialDataType.H3, SpatialDataType.Quadbin]
    } else {
      return []
    }
  }

  const allowedTypes: Set<SpatialDataType> = new Set()
  if (schema.some((field) => spatialIndexSupportedTypes.includes(field.type))) {
    allowedTypes.add(SpatialDataType.H3)
    allowedTypes.add(SpatialDataType.Quadbin)
  }
  if (provider !== ProviderType.DATABRICKS_REST && schema.some((field) => field.type === SchemaFieldType.Geometry)) {
    allowedTypes.add(SpatialDataType.Geo)
  }

  return Array.from(allowedTypes)
}

export function isSchemeAggregated(scheme?: string) {
  return scheme === TilingScheme.H3 || scheme === TilingScheme.Quadbin
}

const tilingSchemeToSpatialDataTypeMapping: Record<string, SpatialDataType> = {
  [TilingScheme.H3]: SpatialDataType.H3,
  [TilingScheme.Quadbin]: SpatialDataType.Quadbin,
  [TilingScheme.Xyz]: SpatialDataType.Geo
}

export function spatialDataTypeFromTilingSchemeTo(scheme?: TilingScheme) {
  return scheme ? tilingSchemeToSpatialDataTypeMapping[scheme] : undefined
}

export function getSpatialDataTypeFromColumnName(column: string) {
  for (const index of Object.keys(SpatialIndexColumn)) {
    if (caseInsensitiveIncludes(SpatialIndexColumn[index], column)) {
      return index as SpatialDataType
    }
  }
  return SpatialDataType.Geo
}
